// Apollo client
import { useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';
// Material ui components
import { Box, CircularProgress, Paper } from '@material-ui/core';
import { useHistory, useParams } from 'react-router-dom';

import { OperationFIDCAdministrator } from './OperationFIDCAdministrator';
import OperationFIDCAsset from './OperationFIDCAsset';
import OperationFIDCGeneralData from './OperationFIDCGeneralData';
import OperationInvestors from './OperationFIDCInvestors';
import OperationFIDCLiability from './OperationFIDCLiability';
import OperationFIDCResume from './OperationFIDCResume';
import OperationFIDCServiceProvider from './OperationFIDCServiceProvider';
import Competence from '../../../components/Competence';
import { If } from '../../../components/If';
import { MonitorFundButton } from '../../../components/MonitorFundButton';
import OperationSubheader from '../../../components/OperationSubheader';
import MetaTags from '../../../components/Seo/MetaTags';
// Global components
import { TabSection } from '../../../components/TabSection';
import ipanema from '../../../graphql/clients/ipanema';
import { GET_FIDC_CADASTRAL_DATA_DETAILS_FROM_MONTH_AND_YEAR_BY_OPERATION_HASH_ID } from '../../../graphql/queries';
import OperationDocuments from '../OperationDocuments';
import OperationRelatedArticles from '../OperationRelatedArticles';
import OperationSecondary from '../OperationSecondary';
import { OperationTimeline } from '../OperationTimeline';
// Local components

export default function OperationFIDC() {
  const { tab, hashId, market, slug } = useParams();
  const history = useHistory();
  const [value, setValue] = useState(
    parseInt(window.localStorage.getItem('operationTabIndex')) || 'resumo',
  );
  const tabs = [
    { label: 'Resumo', value: 'resumo' },
    { label: 'Dados Gerais', value: 'dados' },
    { label: 'Prestadores de Serviço', value: 'prestadores' },
    { label: 'Administrador', value: 'administrador' },
    { label: 'Ativo', value: 'ativo' },
    { label: 'Passivo', value: 'passivo' },
    { label: 'Secundário', value: 'secundario' },
    { label: 'Investidores', value: 'investidores' },
    { label: 'Linha do Tempo', value: 'timeline' },
    { label: 'Documentos', value: 'documentos' },
    { label: 'Artigos Relacionados', value: 'artigos' },
  ];

  const { data, loading } = useQuery(
    GET_FIDC_CADASTRAL_DATA_DETAILS_FROM_MONTH_AND_YEAR_BY_OPERATION_HASH_ID,
    {
      variables: { hash: hashId },
      client: ipanema,
    },
  );

  const handleTabChange = (tabIndex) => {
    setValue(tabIndex);
    if (tabIndex === 'resumo') {
      history.push(`/operacao/${market}/${slug}/${hashId}`);
    } else {
      history.push(`/operacao/${market}/${slug}/${hashId}/${tabIndex}`);
    }
  };

  useEffect(() => {
    window.localStorage.setItem('operationTabIndex', JSON.stringify(value));
    return function clean() {
      localStorage.removeItem('operationTabIndex');
    };
  }, [value]);

  useEffect(() => {
    setValue(tab ?? 'resumo');
  }, []);

  if (loading) return <CircularProgress color="secondary" />;

  const name =
    data?.getFidcCadastralDataDetailsFromMonthAndYearByOperationHashId?.name ??
    '';
  const cnpj =
    data?.getFidcCadastralDataDetailsFromMonthAndYearByOperationHashId?.cnpj ??
    '';

  return (
    <>
      <MetaTags
        title={`${name} - FIDC`}
        description={`Encontre as principais informações sobre ${name}`}
      />
      <OperationSubheader
        title={name}
        market={market.toUpperCase()}
        breadcrumbs1="Fundos"
        breadcrumbs2={`${name} ${market.toUpperCase()}`}
        actions={
          hashId ? (
            <MonitorFundButton
              operationId={hashId}
              operationName={name}
              stepsToShow={['see', 'follow', 'monitor']}
            />
          ) : null
        }
      />
      <Box py={2}>
        <TabSection tabList={tabs} value={value} setValue={handleTabChange}>
          <Paper square>
            <If condition={value === 'resumo'}>
              <Box p={2}>
                <Competence value={hashId} searchType="HASH_ID" />
              </Box>
              <OperationFIDCResume hash={hashId} />
            </If>
            <If condition={value === 'dados'}>
              <OperationFIDCGeneralData hash={hashId} />
            </If>
            <If condition={value === 'prestadores'}>
              <OperationFIDCServiceProvider hash={hashId} />
            </If>
            <If condition={value === 'administrador'}>
              <OperationFIDCAdministrator hash={hashId} />
            </If>
            <If condition={value === 'ativo'}>
              <OperationFIDCAsset hash={hashId} name={name} cnpj={cnpj} />
            </If>
            <If condition={value === 'passivo'}>
              <OperationFIDCLiability hash={hashId} name={name} cnpj={cnpj} />
            </If>
            <If condition={value === 'secundario'}>
              <OperationSecondary hash={hashId} market={market.toUpperCase()} />
            </If>
            <If condition={value === 'documentos'}>
              <OperationDocuments hash={hashId} />
            </If>
            <If condition={value === 'artigos'}>
              <OperationRelatedArticles hash={hashId} />
            </If>
            <If condition={value === 'investidores'}>
              <OperationInvestors
                hash={hashId}
                market={market.toUpperCase()}
                operationCnpj={cnpj}
                operationName={name}
              />
            </If>
            <If condition={value === 'timeline'}>
              <OperationTimeline hashId={hashId} />
            </If>
          </Paper>
        </TabSection>
      </Box>
    </>
  );
}
